/**
 * Staff hub ??workers + admins:
 * register, edit, password, active/inactive, access groups, soft-delete.
 * App and web share the same username + password.
 */
import { useMemo, useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Shield, KeyRound, Plus, Pencil, UserX, UserCheck, Award, X } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import { useAuthStore } from '../../stores/authStore';
import PageHeading from '../../components/PageHeading';
import { InfoButton } from '../../components/InfoModal';
import WorkerIqpPanel from '../../components/WorkerIqpPanel';

const GROUP_COLORS = {
  ADMIN: 'bg-violet-100 text-violet-900 border-violet-200',
  WORKER_INTERNAL: 'bg-sky-100 text-sky-900 border-sky-200',
  WORKER_EXTERNAL: 'bg-amber-100 text-amber-950 border-amber-200',
  GUEST: 'bg-slate-100 text-slate-800 border-slate-200',
};

/** Default rights when assigning an access group (Admin = full transfer including Setup/Lock). */
function capsForGroup(accessGroup) {
  if (accessGroup === 'ADMIN') {
    return {
      can_read: true,
      can_write: true,
      can_delete: true,
      can_setup: true,
      can_lock: true,
    };
  }
  return {
    can_read: true,
    can_write: false,
    can_delete: false,
    can_setup: false,
    can_lock: false,
  };
}

function normalizeFormCaps(c = {}) {
  const out = {
    can_read: !!c.can_read,
    can_write: !!c.can_write,
    can_delete: !!c.can_delete,
    can_setup: !!c.can_setup,
    can_lock: !!c.can_lock,
  };
  if (out.can_delete) {
    out.can_write = true;
    out.can_read = true;
  } else if (out.can_write) {
    out.can_read = true;
  }
  if (out.can_lock) out.can_setup = true;
  return out;
}

function CapBadges({ caps }) {
  if (!caps) return null;
  const bits = [
    caps.can_read && 'Read',
    caps.can_write && 'Edit',
    caps.can_delete && 'Delete',
    caps.can_setup && 'Setup',
    caps.can_lock && 'Lock',
  ].filter(Boolean);
  if (!bits.length) {
    return <span className="text-[10px] text-gray-400">No rights</span>;
  }
  return (
    <span className="inline-flex flex-wrap gap-1">
      {bits.map((b) => (
        <span
          key={b}
          className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-slate-100 text-slate-700"
        >
          {b}
        </span>
      ))}
    </span>
  );
}

/** Detailed access-group help shown via Info (not inline under the title). */
const GROUP_INFO = {
  ADMIN: {
    summary: 'Office / master ??full web & app portfolio',
    body: (
      <div className="space-y-2 text-sm">
        <p>
          <strong>Who:</strong> Office staff and MASTER accounts that run the admin site (admin.cbsl.co.nz) and may also
          use the worker app.
        </p>
        <p>
          <strong>What they can do:</strong> Manage buildings, schedule, inspections, repairing, Registered Company, Staff,
          documents, and settings. Full portfolio visibility.
        </p>
        <p>
          <strong>How to use:</strong> Create as <em>Admin</em> account type (or assign access group Admin). Set a shared
          username/password for web login. MASTER workers also belong here for setup rights.
        </p>
        <p>
          <strong>Tip:</strong> Prefer one login per person so audit logs show who changed schedules and jobs.
        </p>
      </div>
    ),
  },
  WORKER_INTERNAL: {
    summary: 'Cornerstone field IQP ??app inspect',
    body: (
      <div className="space-y-2 text-sm">
        <p>
          <strong>Who:</strong> Cornerstone field IQPs who inspect specified systems on site with the phone app.
        </p>
        <p>
          <strong>What they can do:</strong> Sign into the worker app with the same username/password, receive assigned
          jobs, record Pass/Fail/N/A, and sync when online. Web access is limited unless they are also MASTER.
        </p>
        <p>
          <strong>How to use:</strong> Add staff as <em>Worker</em>, set access group to Worker (internal), add licence /
          IQP number if known, then open <strong>IQP</strong> on the row to attach SS grades/certificates.
        </p>
        <p>
          <strong>Tip:</strong> Keep inactive field staff as Inactive (soft-delete) so history stays linked to their
          jobs.
        </p>
      </div>
    ),
  },
  WORKER_EXTERNAL: {
    summary: 'External contractor / partner IQP',
    body: (
      <div className="space-y-2 text-sm">
        <p>
          <strong>Who:</strong> Partner or contractor IQPs who inspect some SS on our buildings (often linked via Form
          12A / Registered Company), not full Cornerstone employees.
        </p>
        <p>
          <strong>What they can do:</strong> Same app login pattern as internal workers when you issue them an account.
          Access is scoped by how you assign jobs and building rights.
        </p>
        <p>
          <strong>How to use:</strong> Prefer registering the company under Registered Company first. Create a worker
          account here only when they need app login. Set group to Worker (external) so they stay separate from
          internal IQPs.
        </p>
        <p>
          <strong>Tip:</strong> Council register contacts without app login can stay in Registered Company / Registered
          IQP without a Staff account.
        </p>
      </div>
    ),
  },
  GUEST: {
    summary: 'Limited / temporary access',
    body: (
      <div className="space-y-2 text-sm">
        <p>
          <strong>Who:</strong> Temporary or restricted logins (trial, auditor, short-term helper).
        </p>
        <p>
          <strong>What they can do:</strong> Minimal access ??treat as read/limited until you raise their group. Do not
          use Guest for day-to-day field IQPs or office admins.
        </p>
        <p>
          <strong>How to use:</strong> Create the account, set access group Guest, give a short-lived password, then
          either promote to the correct group or Deactivate/Delete when finished.
        </p>
        <p>
          <strong>Tip:</strong> Prefer Inactive over leaving old Guest accounts active.
        </p>
      </div>
    ),
  },
};

const EMPTY_FORM = {
  source: 'worker',
  username: '',
  name: '',
  email: '',
  phone: '',
  address: '',
  license_number: '',
  password: '',
  confirmPassword: '',
  accessGroup: 'WORKER_INTERNAL',
  role: 'ADMIN',
  is_active: true,
  capabilities: capsForGroup('WORKER_INTERNAL'),
};

function workerIdFromPerson(p) {
  if (!p || p.source !== 'worker') return null;
  const key = String(p.person_key || '');
  const m = key.match(/^worker:(.+)$/i);
  if (m) return m[1];
  return p.id != null ? String(p.id) : null;
}

export default function People() {
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const [pwTarget, setPwTarget] = useState(null);
  const [pw1, setPw1] = useState('');
  const [pw2, setPw2] = useState('');
  const [editor, setEditor] = useState(null); // null | { mode: 'create'|'edit', person?, form }
  const [iqpModal, setIqpModal] = useState(null); // null | { workerId, name }
  const [activityFilter, setActivityFilter] = useState('active'); // all | active | inactive

  if (user?.role === 'WORKER') return <Navigate to="/" replace />;

  const { data, isLoading, error } = useQuery({
    queryKey: ['people', 'staff'],
    queryFn: async () => {
      const res = await api.get('/people', {
        params: { includeInactive: 'true' },
      });
      return res.data?.data || { groups: [], people: [], total: 0 };
    },
  });

  const groups = data?.groups || [];
  const people = data?.people || [];

  const filteredPeople = useMemo(() => {
    if (activityFilter === 'active') return people.filter((p) => p.is_active);
    if (activityFilter === 'inactive') return people.filter((p) => !p.is_active);
    return people;
  }, [people, activityFilter]);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['people'] });

  const setGroupMutation = useMutation({
    mutationFn: ({ personKey, accessGroup }) =>
      api.patch('/people/access-group', { personKey, accessGroup }),
    onSuccess: (_, vars) => {
      toast.success(
        vars?.accessGroup === 'ADMIN'
          ? 'Access group → Admin with full rights (Read/Edit/Delete + Setup/Lock). Have them log out and log in again.'
          : 'Access group updated (rights reset to group defaults). They may need to log in again.'
      );
      invalidate();
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Update failed'),
  });

  const passwordMutation = useMutation({
    mutationFn: ({ personKey, password }) =>
      api.patch('/people/password', { personKey, password }),
    onSuccess: () => {
      toast.success('Password updated ?? same for web and app');
      setPwTarget(null);
      setPw1('');
      setPw2('');
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Password update failed'),
  });

  const saveMutation = useMutation({
    mutationFn: async ({ mode, personKey, form }) => {
      if (mode === 'create') {
        const body = {
          source: form.source,
          username: form.username.trim(),
          name: form.name.trim(),
          email: form.email.trim() || null,
          phone: form.phone.trim() || null,
          address: form.address.trim() || null,
          license_number: form.license_number.trim() || null,
          password: form.password,
          accessGroup: form.accessGroup,
          is_active: form.is_active,
        };
        if (form.source === 'admin') body.role = form.role;
        body.capabilities = normalizeFormCaps(form.capabilities);
        const res = await api.post('/people', body);
        return res.data?.data;
      }
      const body = {
        username: form.username.trim(),
        name: form.name.trim(),
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        address: form.address.trim() || null,
        license_number: form.license_number.trim() || null,
        accessGroup: form.accessGroup,
        is_active: form.is_active,
        capabilities: normalizeFormCaps(form.capabilities),
      };
      if (form.source === 'admin') body.role = form.role;
      if (form.password) body.password = form.password;
      const res = await api.patch(`/people/${encodeURIComponent(personKey)}`, body);
      return res.data?.data;
    },
    onSuccess: (_, vars) => {
      toast.success(vars.mode === 'create' ? 'Person created' : 'Person updated');
      setEditor(null);
      invalidate();
      queryClient.invalidateQueries({ queryKey: ['workers'] });
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Save failed'),
  });

  const activeMutation = useMutation({
    mutationFn: ({ personKey, is_active }) =>
      api.patch('/people/active', { personKey, is_active }),
    onSuccess: (_, vars) => {
      toast.success(vars.is_active ? 'Activated' : 'Deactivated');
      invalidate();
      queryClient.invalidateQueries({ queryKey: ['workers'] });
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Status update failed'),
  });

  const deleteMutation = useMutation({
    mutationFn: (personKey) => api.delete(`/people/${encodeURIComponent(personKey)}`),
    onSuccess: () => {
      toast.success('Account deactivated (username freed)');
      invalidate();
      queryClient.invalidateQueries({ queryKey: ['workers'] });
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Delete failed'),
  });

  const byGroup = useMemo(() => {
    const map = {};
    for (const g of groups) map[g.key] = [];
    for (const p of filteredPeople) {
      if (!map[p.access_group]) map[p.access_group] = [];
      map[p.access_group].push(p);
    }
    return map;
  }, [groups, filteredPeople]);

  const groupCounts = useMemo(() => {
    const counts = {};
    for (const g of groups) counts[g.key] = (byGroup[g.key] || []).length;
    return counts;
  }, [groups, byGroup]);

  const ACTIVITY_OPTS = [
    { id: 'all', label: 'All' },
    { id: 'active', label: 'Active' },
    { id: 'inactive', label: 'Inactive' },
  ];

  const openCreate = () => {
    setEditor({
      mode: 'create',
      form: { ...EMPTY_FORM, capabilities: capsForGroup('WORKER_INTERNAL') },
    });
  };

  const openEdit = (p) => {
    setEditor({
      mode: 'edit',
      person: p,
      form: {
        source: p.source,
        username: p.username || '',
        name: p.name || '',
        email: p.email || '',
        phone: p.phone || '',
        address: p.address || '',
        license_number: p.license_number || '',
        password: '',
        confirmPassword: '',
        accessGroup: p.access_group,
        role: p.source === 'admin' ? p.auth_role || 'ADMIN' : 'ADMIN',
        is_active: !!p.is_active,
        capabilities: normalizeFormCaps(p.capabilities || capsForGroup(p.access_group)),
      },
    });
  };

  const submitEditor = (e) => {
    e.preventDefault();
    if (!editor) return;
    const { form, mode, person } = editor;
    if (!form.username.trim() || !form.name.trim()) {
      toast.error('Username and name are required');
      return;
    }
    if (mode === 'create' && form.password.length < 4) {
      toast.error('Password must be at least 4 characters');
      return;
    }
    if (form.password && form.password !== form.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (form.password && form.password.length > 0 && form.password.length < 4) {
      toast.error('Password must be at least 4 characters');
      return;
    }
    saveMutation.mutate({
      mode,
      personKey: person?.person_key,
      form,
    });
  };

  const submitPassword = (e) => {
    e.preventDefault();
    if (!pwTarget) return;
    if (pw1.length < 4) {
      toast.error('Password must be at least 4 characters');
      return;
    }
    if (pw1 !== pw2) {
      toast.error('Passwords do not match');
      return;
    }
    passwordMutation.mutate({ personKey: pwTarget.person_key, password: pw1 });
  };

  const setForm = (patch) => {
    setEditor((prev) => (prev ? { ...prev, form: { ...prev.form, ...patch } } : prev));
  };

  return (
    <div>
      <PageHeading
        title="Staff"
        subPages={[
          { id: 'staff', label: 'Staff', to: '/people', active: true },
          { id: 'shelf', label: 'App shelf', to: '/people/app-shelf' },
        ]}
        infoTitle="Staff"
        infoChildren={
          <div className="space-y-3 text-sm">
            <div>
              <p className="font-semibold text-gray-900">Concepts</p>
              <p className="mt-1 text-gray-700">
                Registered person = one login row (worker or admin). App user = worker account signing into the phone
                with the same username/password. Web user = admin (or master worker) on admin.cbsl.co.nz. Manage all of
                them on this page.
              </p>
            </div>
            <p>
              <strong>Username</strong> = app &amp; web login ID. <strong>Password</strong> is shared. Add/Edit uses one
              profile form (name, email, phone, address, licence/IQP #, access group) for admins and workers.
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>
                <strong>Login type</strong> ??Worker (app / field) or Admin (office web); profile fields are the same
              </li>
              <li>
                <strong>Access group</strong> — Admin / Worker internal / Worker external / Guest
              </li>
              <li>
                <strong>Rights</strong> — Read / Edit / Delete (office) + Setup / Lock (unit setup). Assigning Admin
                grants all five by default so authority can transfer fully.
              </li>
              <li>
                <strong>Active</strong> — inactive accounts cannot log in
              </li>
              <li>
                <strong>IQP / SS</strong> ??SS grades on Worker accounts (inline in Edit, or row button)
              </li>
            </ul>
          </div>
        }
        actions={
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={openCreate}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700"
            >
              <Plus className="w-4 h-4" /> Add staff
            </button>
            <Link to="/admins" className="text-sm text-gray-500 hover:underline inline-flex items-center gap-1">
              <Shield className="w-4 h-4" /> Admins (legacy)
            </Link>
          </div>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {groups.map((g) => {
          const info = GROUP_INFO[g.key];
          return (
            <div key={g.key} className={`rounded-lg border p-3 text-left ${GROUP_COLORS[g.key] || ''}`}>
              <div className="flex items-baseline gap-1">
                <p className="text-sm font-bold">{g.label}</p>
                {info && (
                  <InfoButton title={g.label} label="Info" className="text-xs shrink-0 opacity-80 hover:opacity-100">
                    <p className="text-xs font-medium text-gray-500 mb-2">{info.summary}</p>
                    {info.body}
                  </InfoButton>
                )}
              </div>
              <p className="text-2xl font-bold mt-1">{groupCounts[g.key] ?? 0}</p>
            </div>
          );
        })}
      </div>

      {isLoading && <p className="text-sm text-gray-500">Loading…</p>}
      {error && (
        <p className="text-sm text-red-600">{error.response?.data?.error || error.message || 'Failed to load'}</p>
      )}

      {!isLoading && !error && (
        <div className="space-y-6">
          {groups.map((g) => {
            const rows = byGroup[g.key] || [];
            return (
              <section key={g.key} className="rounded-xl border border-gray-200 bg-white overflow-hidden">
                <div
                  className={`px-4 py-3 border-b flex flex-wrap items-center justify-between gap-2 ${
                    GROUP_COLORS[g.key] || 'bg-gray-50'
                  }`}
                >
                  <div className="min-w-0 flex items-center gap-2">
                    <h2 className="font-semibold text-gray-900">{g.label}</h2>
                    {GROUP_INFO[g.key] && (
                      <InfoButton title={g.label} label="Info" className="text-xs text-gray-600 hover:text-gray-900">
                        <p className="text-xs font-medium text-gray-500 mb-2">{GROUP_INFO[g.key].summary}</p>
                        {GROUP_INFO[g.key].body}
                      </InfoButton>
                    )}
                  </div>
                  <div className="flex items-center gap-1 border border-gray-300/80 rounded-lg p-0.5 bg-white/90 shrink-0">
                    {ACTIVITY_OPTS.map((opt) => (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => setActivityFilter(opt.id)}
                        className={`px-2.5 py-1 rounded-md text-xs font-semibold transition-colors ${
                          activityFilter === opt.id
                            ? 'bg-navy-900 text-white shadow-sm'
                            : 'text-gray-600 hover:text-gray-900 hover:bg-white'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
                {rows.length === 0 ? (
                  <p className="p-4 text-sm text-gray-400">
                    {activityFilter === 'inactive'
                      ? 'No inactive accounts in this group.'
                      : activityFilter === 'active'
                        ? 'No active accounts in this group.'
                        : 'No one in this group yet.'}
                  </p>
                ) : (
                  <ul className="divide-y divide-gray-100">
                    {rows.map((p) => {
                      const wid = workerIdFromPerson(p);
                      return (
                        <li key={p.person_key} className="px-4 py-3 flex flex-wrap items-center gap-2">
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-gray-900 truncate">
                              {wid ? (
                                <Link
                                  to={`/workers/${wid}`}
                                  className="text-primary-800 hover:underline"
                                  title="Open staff settings — buildings this person covers"
                                >
                                  {p.name || p.username}
                                </Link>
                              ) : (
                                p.name || p.username
                              )}
                              {!p.is_active && (
                                <span className="ml-2 text-[10px] uppercase tracking-wide text-red-700 bg-red-50 px-1.5 py-0.5 rounded">
                                  inactive
                                </span>
                              )}
                            </p>
                            <p className="text-xs text-gray-500 truncate">
                              Login ID: <span className="font-semibold text-gray-700">{p.username}</span>
                              {p.email ? ` · ${p.email}` : ''}
                              {' · '}
                              {p.source === 'admin' ? 'Admin account' : 'Worker / app account'}
                              {p.is_master ? ' · MASTER' : ''}
                              {p.auth_role ? ` · login as ${p.auth_role}` : ''}
                              {p.license_number ? ` · Lic. ${p.license_number}` : ''}
                              {p.address ? ` · ${p.address}` : ''}
                            </p>
                            <div className="mt-1">
                              <CapBadges caps={p.capabilities} />
                            </div>
                          </div>
                          <select
                            value={p.access_group}
                            disabled={setGroupMutation.isPending}
                            onChange={(e) =>
                              setGroupMutation.mutate({
                                personKey: p.person_key,
                                accessGroup: e.target.value,
                              })
                            }
                            className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm bg-white"
                            title="Access group"
                          >
                            {groups.map((opt) => (
                              <option key={opt.key} value={opt.key}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                          {wid && (
                            <button
                              type="button"
                              onClick={() =>
                                setIqpModal({ workerId: wid, name: p.name || p.username })
                              }
                              className="inline-flex items-center gap-1 text-xs font-medium text-sky-800 hover:underline"
                            >
                              <Award className="w-3.5 h-3.5" /> IQP / SS
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => openEdit(p)}
                            className="inline-flex items-center gap-1 text-xs font-medium text-primary-700 hover:underline"
                          >
                            <Pencil className="w-3.5 h-3.5" /> Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setPwTarget(p);
                              setPw1('');
                              setPw2('');
                            }}
                            className="inline-flex items-center gap-1 text-xs font-medium text-amber-800 hover:underline"
                          >
                            <KeyRound className="w-3.5 h-3.5" /> Password
                          </button>
                          <button
                            type="button"
                            disabled={activeMutation.isPending || p.is_master}
                            onClick={() =>
                              activeMutation.mutate({
                                personKey: p.person_key,
                                is_active: !p.is_active,
                              })
                            }
                            className="inline-flex items-center gap-1 text-xs font-medium text-slate-700 hover:underline disabled:opacity-40"
                            title={p.is_master ? 'MASTER cannot be deactivated here' : ''}
                          >
                            {p.is_active ? (
                              <>
                                <UserX className="w-3.5 h-3.5" /> Deactivate
                              </>
                            ) : (
                              <>
                                <UserCheck className="w-3.5 h-3.5" /> Activate
                              </>
                            )}
                          </button>
                          <button
                            type="button"
                            disabled={deleteMutation.isPending || p.is_master}
                            className="text-xs text-red-700 hover:underline disabled:opacity-40"
                            onClick={() => {
                              if (
                                window.confirm(
                                  `Deactivate ${p.name || p.username} and free the username? They will not be able to log in.`
                                )
                              ) {
                                deleteMutation.mutate(p.person_key);
                              }
                            }}
                          >
                            Delete
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </section>
            );
          })}
        </div>
      )}

      {editor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <form
            onSubmit={submitEditor}
            className="bg-card rounded-xl shadow-xl max-w-2xl w-full p-5 space-y-4 max-h-[90vh] overflow-y-auto"
          >
            <div>
              <h3 className="font-semibold text-gray-900">
                {editor.mode === 'create' ? 'Add staff' : 'Edit staff'}
              </h3>
              <p className="text-xs text-gray-500 mt-1">
                Same profile for office admins and field workers ?? username is the login ID for web and app.
              </p>
              {editor.mode === 'edit' && editor.person && (
                <p className="text-xs font-medium text-gray-700 mt-1">
                  Account:{' '}
                  <span className="text-primary-800">
                    {editor.person.source === 'admin' ? 'Admin (office web)' : 'Worker (app / field)'}
                  </span>
                  {editor.person.is_master ? ' · MASTER' : ''}
                </p>
              )}
            </div>

            {editor.mode === 'create' && (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Login type</label>
                <select
                  value={editor.form.source}
                  onChange={(e) => {
                    const source = e.target.value;
                    const accessGroup = source === 'admin' ? 'ADMIN' : 'WORKER_INTERNAL';
                    setForm({
                      source,
                      accessGroup,
                      capabilities: capsForGroup(accessGroup),
                    });
                  }}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                >
                  <option value="worker">Worker (app / field)</option>
                  <option value="admin">Admin (office web)</option>
                </select>
                <p className="text-[11px] text-gray-500 mt-1">
                  Login type sets where this username signs in. Profile fields below are the same for both.
                </p>
              </div>
            )}

            <div className="rounded-lg border border-gray-200 p-3 space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Contact</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Username (login ID) *</label>
                  <input
                    value={editor.form.username}
                    onChange={(e) => setForm({ username: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    required
                    autoComplete="off"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Full name *</label>
                  <input
                    value={editor.form.name}
                    onChange={(e) => setForm({ name: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
                  <input
                    type="email"
                    value={editor.form.email}
                    onChange={(e) => setForm({ email: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Phone</label>
                  <input
                    value={editor.form.phone}
                    onChange={(e) => setForm({ phone: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Address</label>
                  <textarea
                    value={editor.form.address}
                    onChange={(e) => setForm({ address: e.target.value })}
                    rows={2}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-y"
                    placeholder="Street, suburb, city"
                  />
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-gray-200 p-3 space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Access &amp; IQP</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Licence / IQP #</label>
                  <input
                    value={editor.form.license_number}
                    onChange={(e) => setForm({ license_number: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    placeholder="Council register number"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Access group</label>
                  <select
                    value={editor.form.accessGroup}
                    onChange={(e) => {
                      const accessGroup = e.target.value;
                      setForm({
                        accessGroup,
                        capabilities: capsForGroup(accessGroup),
                      });
                    }}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  >
                    {groups.map((g) => (
                      <option key={g.key} value={g.key}>
                        {g.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Web admin role</label>
                  <select
                    value={editor.form.role}
                    onChange={(e) => setForm({ role: e.target.value })}
                    disabled={editor.form.source !== 'admin'}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm disabled:bg-gray-100 disabled:text-gray-500"
                  >
                    <option value="SUPER_ADMIN">SUPER_ADMIN</option>
                    <option value="ADMIN">ADMIN</option>
                    <option value="MANAGER">MANAGER</option>
                  </select>
                  {editor.form.source !== 'admin' && (
                    <p className="text-[11px] text-gray-500 mt-1">Office web role — Admin login type only.</p>
                  )}
                </div>
              </div>

              <div className="pt-2 border-t border-gray-100 space-y-2">
                <p className="text-xs font-semibold text-gray-800">Rights</p>
                <p className="text-[11px] text-gray-500">
                  Read / Edit / Delete control office changes. Setup / Lock control unit setup on the app. Choosing
                  Admin presets all five so you can transfer full authority.
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                  {[
                    { key: 'can_read', label: 'Read' },
                    { key: 'can_write', label: 'Edit' },
                    { key: 'can_delete', label: 'Delete' },
                    { key: 'can_setup', label: 'Setup' },
                    { key: 'can_lock', label: 'Lock' },
                  ].map((opt) => (
                    <label
                      key={opt.key}
                      className="inline-flex items-center gap-2 text-sm text-gray-800 border border-gray-200 rounded-lg px-2.5 py-2 bg-white cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={!!editor.form.capabilities?.[opt.key]}
                        disabled={!!editor.person?.is_master}
                        onChange={(e) => {
                          const next = {
                            ...(editor.form.capabilities || capsForGroup(editor.form.accessGroup)),
                            [opt.key]: e.target.checked,
                          };
                          setForm({ capabilities: normalizeFormCaps(next) });
                        }}
                      />
                      {opt.label}
                    </label>
                  ))}
                </div>
                {!editor.form.email?.trim() &&
                  (editor.form.capabilities?.can_setup || editor.form.capabilities?.can_lock) && (
                    <p className="text-[11px] text-amber-800">
                      Add an email so Setup / Lock can sync to the field app grant list.
                    </p>
                  )}
              </div>
            </div>

            <div className="rounded-lg border border-amber-100 bg-amber-50/50 p-3 space-y-2">
              <p className="text-xs font-medium text-amber-950">
                Password {editor.mode === 'create' ? '*' : '(leave blank to keep)'}
              </p>
              <input
                type="password"
                value={editor.form.password}
                onChange={(e) => setForm({ password: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
                placeholder={editor.mode === 'create' ? 'New password' : 'Optional new password'}
                required={editor.mode === 'create'}
                autoComplete="new-password"
              />
              {(editor.form.password || editor.mode === 'create') && (
                <input
                  type="password"
                  value={editor.form.confirmPassword}
                  onChange={(e) => setForm({ confirmPassword: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
                  placeholder="Confirm password"
                  required={editor.mode === 'create' || !!editor.form.password}
                />
              )}
            </div>

            {editor.mode === 'edit' && workerIdFromPerson(editor.person) && (
              <div className="rounded-lg border border-sky-200 bg-sky-50/30 p-3 space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-sky-900">SS certificates</p>
                <WorkerIqpPanel workerId={workerIdFromPerson(editor.person)} compact />
              </div>
            )}

            {editor.mode === 'edit' && editor.person?.source === 'admin' && (
              <div className="rounded-lg border border-amber-100 bg-amber-50/40 p-3">
                <p className="text-xs text-amber-950">
                  <strong>App inspection / SS grades:</strong> Admin accounts use the office web. To assign SS
                  certificates for the phone app, add a separate <strong>Worker (app / field)</strong> login for this
                  person (or convert later when account types are merged).
                </p>
              </div>
            )}

            <label className="text-sm inline-flex items-center gap-2">
              <input
                type="checkbox"
                checked={editor.form.is_active}
                onChange={(e) => setForm({ is_active: e.target.checked })}
              />
              Active (can log in)
            </label>

            <div className="flex justify-end gap-2 pt-1">
              <button
                type="button"
                className="px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg"
                onClick={() => setEditor(null)}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saveMutation.isPending}
                className="px-4 py-2 text-sm font-semibold rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
              >
                {saveMutation.isPending ? 'Saving…' : 'Save'}
              </button>
            </div>
          </form>
        </div>
      )}

      {iqpModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4">
          <div className="bg-card rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-gray-100">
              <div>
                <h3 className="font-semibold text-gray-900">IQP / SS certificates</h3>
                <p className="text-sm text-gray-600">{iqpModal.name}</p>
              </div>
              <button
                type="button"
                onClick={() => setIqpModal(null)}
                className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 overflow-y-auto flex-1">
              <WorkerIqpPanel workerId={iqpModal.workerId} compact />
            </div>
            <div className="px-5 py-3 border-t border-gray-100 flex justify-end">
              <button
                type="button"
                onClick={() => setIqpModal(null)}
                className="px-4 py-2 text-sm font-medium rounded-lg bg-gray-100 hover:bg-gray-200"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {pwTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <form
            onSubmit={submitPassword}
            className="bg-card rounded-xl shadow-xl max-w-md w-full p-5 space-y-3"
          >
            <h3 className="font-semibold text-gray-900">Set login password</h3>
            <p className="text-sm text-gray-600">
              For <strong>{pwTarget.name || pwTarget.username}</strong> ??login ID:{' '}
              <code className="text-xs bg-gray-100 px-1 rounded">{pwTarget.username}</code>
            </p>
            <input
              type="password"
              value={pw1}
              onChange={(e) => setPw1(e.target.value)}
              placeholder="New password"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              autoFocus
            />
            <input
              type="password"
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
              placeholder="Confirm password"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
            <div className="flex justify-end gap-2 pt-1">
              <button
                type="button"
                className="px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg"
                onClick={() => setPwTarget(null)}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={passwordMutation.isPending}
                className="px-4 py-2 text-sm font-semibold rounded-lg bg-amber-700 text-white hover:bg-amber-800 disabled:opacity-50"
              >
                Save password
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

